import { Component, Element, Host, Listen, Prop, State, Watch, h } from '@stencil/core';
import { getAPI, type UserAPI } from '@sydle/ui-api';
import { BlogPost } from '../../../types/blog';
import { syQuerySelector } from '../../../utils/dom.utils';
import { renderComponent } from '../../../utils/render-utils';
import { ComponentContentType } from '../sy-blog-share-bar/share-bar.types';
import { Consumer } from '@sydle/ui-utils';
import { contentEvent } from '../../../utils/tracking-utils';
@Component({
  tag: 'sy-blog-post-page',
  styleUrl: 'scss/index.scss',
  scoped: true,
})
export class SyBlogPostPage {
  private consumer: Consumer;
  @Element() el: HTMLSyBlogPostPageElement;

  /**
   * Post body
   */
  @Prop() postBody: BlogPost;

  /**
   * share bar JSON component Prop
   */
  @Prop() shareComponentJson: ComponentContentType;

  /**
   * summary JSON component Prop
   */
  @Prop() summaryComponentJson: ComponentContentType;

  /**
   * Banner JSON component Prop
   */
  @Prop() bannerComponentJson: ComponentContentType;

  /**
   * Defines the offset from the top when scrolling to a section.
   * Default value is 53.
   */
  @Prop() scrollOffset = 53;

  @State() isLoading = true;
  @State() isDraft: string;
  @State() ctaURL;

  private contentEl: HTMLSyBlogContentElement;

  @Watch('postBody')
  postBodyWatcher(): void {
    if (this.postBody) {
      contentEvent(this.postBody);
    }
  }

  @Listen('syBlogSummaryNodeDidClick')
  onSyBlogSummaryNodeDidClick(event: CustomEvent): void {
    const sectionId = event.detail;
    this.contentEl.navigateToSection(sectionId);
    return event.detail;
  }

  /**
   * Scrolls to the associated section when clicked.
   */
  private scrollToSection(e: Event, section: HTMLElement): void {
    e.preventDefault();

    // Calculate the scroll position and scroll smoothly to it
    window.scrollTo({
      top: section.getBoundingClientRect().top + window.scrollY - this.scrollOffset,
      behavior: 'smooth',
    });
  }

  componentDidLoad() {
    if (window.location.hash) {
      const targetElement = syQuerySelector(window.location.hash) as HTMLElement;
      if (targetElement) {
        window.scrollTo({
          top: targetElement.getBoundingClientRect().top - this.scrollOffset,
          behavior: 'smooth',
        });
      }
    }

    // Select the <a> tags that have a href with a hash and add a click event to scroll to the section.
    document.body.querySelectorAll('a[href*="#"]').forEach((anchor: HTMLAnchorElement) => {
      if (anchor.href.includes(this.postBody.identifier)) {
        const targetElement = syQuerySelector('#' + anchor.href.split('#')[1]) as HTMLElement;
        anchor.addEventListener('click', (e) => this.scrollToSection(e, targetElement));
      }
    });
  }

  componentDidUpdate() {
    window.scrollTo({
      top: 0,
    });

    if (window.location.hash) {
      const targetElement = syQuerySelector(window.location.hash) as HTMLElement;

      if (targetElement) {
        setTimeout(() => {
          window.scrollTo({
            top: targetElement.getBoundingClientRect().top - this.scrollOffset,
            behavior: 'smooth',
          });
        }, 500);
      }
    }
  }

  async componentWillRender() {
    if (this.postBody) {
      this.postBody.content = this.searchInstagramPost(this.postBody.content);
      this.isLoading = false;
    }
  }

  connectedCallback() {
    this.consumer = new Consumer(this.el);
    this.consumer.subscribe('postBody', (post: BlogPost) => (this.postBody = post));
  }

  disconnectedCallback() {
    this.consumer.disconnect();
  }

  // Function to search for an Instagram post in a blog post, and if it found it, remove the post script and insert it in the page head.
  private searchInstagramPost(postContent: string) {
    const regexResult = postContent.match(
      /<script\s+(?:async\s+)?src\s*=\s*"(?:https?:)?\/\/www\.instagram\.com\/embed\.js"><\/script>/g,
    );
    if (regexResult && regexResult?.length > 0) {
      const embed = '//www.instagram.com/embed.js';
      const scriptElement = document.createElement('script');
      scriptElement.src = embed;
      document.head.appendChild(scriptElement);
      let newPostContent;
      regexResult.forEach((script) => {
        newPostContent = postContent.replace(script, '');
      });
      return newPostContent;
    }
    return postContent;
  }

  componentDidRender() {
    if (!this.isLoading) {
      this.el.style.visibility = 'inherit';
    }
    (window as any).instgrm?.Embeds.process(); //script to load instagram posts
  }

  render() {
    return !this.postBody ? (
      <Host>
        <sy-grid type="flex">
          <sy-row>
            <sy-col size="12" size-lg="8" offset-lg="2" class="content">
              {Array(20)
                .fill(0)
                .map((_, i) => (
                  <sy-blog-skeleton-load key={i} />
                ))}
            </sy-col>
          </sy-row>
        </sy-grid>
      </Host>
    ) : (
      <Host style={{ visibility: 'hidden' }}>
        <sy-grid type="flex" fluid="true">
          <sy-row>
            <sy-col offset-xl="2" offset-sm="0" size-xl="8" sixe-sm="12" class="text-content-col">
              <div class="d-flex flex-column">
                <div class="text-size-container">
                  <div class="sticky-top">
                    {this.shareComponentJson ? renderComponent(this.shareComponentJson) : null}
                  </div>

                  {this.summaryComponentJson ? renderComponent(this.summaryComponentJson) : null}

                  <sy-blog-content
                    header-height="53"
                    content={
                      !getAPI<UserAPI>('userAPI')?.getUser() &&
                      this.postBody.classification === 'Blog Post (Acesso restrito)'
                        ? this.postBody.preview
                        : this.postBody.content
                    }
                  />

                  <div class="tags">
                    {this.postBody.tags.map((tag) => {
                      const href = tag.url;
                      return (
                        <sy-tag key={tag.identifier} href={href}>
                          {tag.name}
                        </sy-tag>
                      );
                    })}
                  </div>
                </div>
              </div>
            </sy-col>
            <sy-col size-xl="2" size-sm="12" class="desktop-banner">
              <div class="lateral-banner">
                {this.bannerComponentJson ? renderComponent(this.bannerComponentJson) : null}
              </div>
            </sy-col>
          </sy-row>
        </sy-grid>
      </Host>
    );
  }
}
